<template>
  <v-dialog v-model="show" persistent max-width="600" mobile-breakpoint="0">
    <v-card>
      <v-toolbar class="mb-6">
        <v-toolbar-title data-testid="toolbar-title"><span class="text-h5">{{ $t('announce.edit.title') }}</span></v-toolbar-title>
      </v-toolbar>
      <v-spacer></v-spacer>
      <v-card-text>
        <v-text-field v-model="name" :rules="[rules.required]"
          :label="$t('announce.edit.name')">
        </v-text-field>
        <v-text-field v-model="pin" @click:append="showPIN=!showPIN"
          :append-icon="showPIN ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPIN ? 'text' : 'password'"
          :rules="[rules.required,rules.numeric]"
          :label="$t('announce.edit.pin')">
        </v-text-field>
        <v-tabs v-model="recTab" grow>
          <v-tab href="#REC">
            {{ $t('announce.tabRec') }}
          </v-tab>
          <v-tab href="#T2S">
            {{ $t('announce.tabT2S') }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="recTab">
          <v-tab-item value="REC">
            <Recording :src="srcURL" @update-src="updateURL"/>
          </v-tab-item>
          <v-tab-item value="T2S">
            <v-row class="mt-1">
              <tts-form :speed="Number(speed)" :pitch="pitch"  :volumeGainDb="volumeGainDb" :textToSpeech="textToSpeech"
              :voiceType="voiceType" :textType="textType" @update-voice-type="updateVoiceType" @update-speed="updateSpeed"
              @update-pitch="updatePitch" @update-text-type="updateTextType"
              @update-text-to-speech="updateTextToSpeech" @update-volume-gain-db="updateVolumeGainDb"/>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancel" text>
          {{ $t('announce.edit.cancel') }}
        </v-btn>
        <v-btn @click="update" color="primary" text :disabled="!canSubmit">
          {{ $t('announce.edit.update') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import API from '@/api'
import events from 'Store/events'
import Recording from './recording'
import TtsForm from '@/components/text_to_speech'

const TTS_MAX_CHARS = 2000

export default {
  name: 'announcements-editor',
  data () {
    return {
      name: '',
      lang: '',
      pin: '',
      textToSpeech: '',
      speed: 1,
      pitch: 0,
      volumeGainDb: 0,
      textType: '',
      voiceType: '',
      rules: {
        required: v => !!v || this.$t('announce.edit.required'),
        numeric: v => /^\d+$/.test(v) || this.$t('announce.edit.numeric')
      },
      newSrcURL: null,
      recTab: null,
      showPIN: false
    }
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object
    },
    srcURL: {
      type: String
    }
  },
  mounted () {
    this.setFromData()
  },
  components: { Recording, TtsForm },
  computed: {
    title: function () {
      return this.data?.name
    },
    canSubmit () {
      if (this.textToSpeech) {
        return this.name && this.pin && this.speed && this.pitch >= -20 && this.volumeGainDb >= -96 && this.textType && this.voiceType && this.textToSpeech.length <= TTS_MAX_CHARS
      } else {
        return this.name && this.pin
      }
    }
  },
  methods: {
    ...mapActions({
      updateRec: 'announcements/updateRec',
      load: 'announcements/load'
    }),
    cancel: function () {
      this.setFromData()
      this.$emit('close')
    },
    setFromData: function () {
      this.name = this.data.name
      this.pin = this.data.pin
      this.speed = this.data.speed
      this.pitch = this.data.pitch
      this.volumeGainDb = this.data.volumeGainDb
      this.textToSpeech = this.data.textToSpeech
      this.voiceType = this.data.voiceType
      this.textType = this.data.textType
      this.recTab = 'REC'
      this.newSrcURL = null
    },
    updateURL: function (val) { this.newSrcURL = val },
    updateSpeed: function (speed) { this.speed = speed },
    updatePitch: function (pitch) { this.pitch = pitch },
    updateVolumeGainDb: function (volumeGainDb) { this.volumeGainDb = volumeGainDb },
    updateTextToSpeech: function (textToSpeech) { this.textToSpeech = textToSpeech },
    updateTextType: function (textType) { this.textType = textType },
    updateVoiceType: function (voiceType) { this.voiceType = voiceType },
    update: function () {
      const t2s = this.textToSpeech?.trim()
      if (this.recTab === 'T2S' && t2s.length > TTS_MAX_CHARS) {
        events.pubError(this.$t('announce.edit.ttsMaxLength', { max: TTS_MAX_CHARS }))
        return
      }
      const data = {
        name: this.name,
        pin: this.pin
      }
      if (this.recTab === 'T2S' && t2s.length > 0) {
        data.text_to_speech = t2s
        data.speed = this.speed
        data.pitch = this.pitch
        data.volume_gain_db = this.volumeGainDb
        data.text_type = this.textType
        data.voice_type = this.voiceType
      }
      events.mask()
      API.updateAnnouncement(this.data.id, data).then((resp) => {
        this.dbg('Updated data: %o', resp?.data)
        this.updateRec({ id: this.data.id, data: data })
        if (this.recTab === 'REC' && this.newSrcURL) {
          this.dbg('Upload new recording', this.newSrcURL)
          return API.uploadAnnouncement(this.data.id, this.newSrcURL)
        }
      }).catch(err => {
        this.dbg('Failed to update announcement')
        this.dbg(err)
        events.pubError(API.getException(err) || 'Failed to update announcement')
      }).finally(() => {
        events.unmask()
        this.$emit('updated')
        this.$emit('close')
        this.load()
      })
    }
  }
}
</script>

<style>
</style>
